import type { TimelinePerson } from "@/hooks/useTimelineData";

type Props = {
  people: TimelinePerson[];
  visiblePeople: string[];
  onToggle: (id: string) => void;
  onSelectAll: () => void;
  onSelectNone: () => void;
};

function getInitials(name: string): string {
  return name
    .split(" ")
    .map((part) => part[0])
    .join("")
    .slice(0, 2)
    .toUpperCase();
}

export function PeopleSidebar({
  people,
  visiblePeople,
  onToggle,
  onSelectAll,
  onSelectNone,
}: Props) {
  return (
    <div className="w-56 bg-surface border-r border-border flex flex-col">
      <div className="p-3 border-b border-border">
        <h2 className="text-foreground font-medium text-sm mb-2">People</h2>
        <div className="flex gap-1.5">
          <button
            onClick={onSelectAll}
            className="flex-1 px-2 py-1 text-[10px] bg-surface-elevated hover:bg-border text-muted rounded transition-colors"
          >
            Select All
          </button>
          <button
            onClick={onSelectNone}
            className="flex-1 px-2 py-1 text-[10px] bg-surface-elevated hover:bg-border text-muted rounded transition-colors"
          >
            Clear
          </button>
        </div>
      </div>

      <div className="flex-1 overflow-y-auto p-1.5">
        {people.map((person, idx) => {
          const isVisible = visiblePeople.includes(person.id);
          const hue = 170 + (idx * 15) % 40;

          return (
            <label
              key={person.id}
              className={`flex items-center gap-2 px-2 py-1.5 rounded cursor-pointer transition-colors ${
                isVisible ? "bg-surface-elevated" : "hover:bg-surface-elevated/50"
              }`}
            >
              <input
                type="checkbox"
                checked={isVisible}
                onChange={() => onToggle(person.id)}
                className="sr-only"
              />
              {/* Photo thumbnail */}
              {person.imageUrl ? (
                <img
                  src={person.imageUrl}
                  alt={person.name}
                  className="w-8 h-8 rounded-full object-cover flex-shrink-0"
                />
              ) : (
                <div
                  className="w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 text-[10px] font-medium text-white"
                  style={{ backgroundColor: `hsl(${hue}, 40%, 35%)` }}
                >
                  {getInitials(person.name)}
                </div>
              )}
              {/* Checkbox indicator */}
              <div
                className={`w-3 h-3 rounded-sm border flex items-center justify-center transition-colors flex-shrink-0`}
                style={{
                  borderColor: `hsl(${hue}, 40%, 45%)`,
                  backgroundColor: isVisible
                    ? `hsl(${hue}, 40%, 45%)`
                    : "transparent",
                }}
              >
                {isVisible && (
                  <svg
                    className="w-2 h-2 text-white"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={3}
                      d="M5 13l4 4L19 7"
                    />
                  </svg>
                )}
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-xs text-foreground truncate font-editorial">{person.name}</p>
                <p className="text-[10px] text-muted-foreground">
                  {person.birthDate
                    ? new Date(person.birthDate).getFullYear()
                    : "?"}
                  –
                  {person.deathDate
                    ? new Date(person.deathDate).getFullYear()
                    : "now"}
                </p>
              </div>
            </label>
          );
        })}
      </div>

      <div className="p-2 border-t border-border text-[10px] text-muted-foreground">
        {visiblePeople.length}/{people.length} selected
      </div>
    </div>
  );
}
